import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Snackbar,
  Alert,
} from "@mui/material";
import { isDarkMode } from "../../utils/constants";
import {
  clearUserTradeProposals,
  getUserTradeProposals,
  updateTradeProposalTracking,
} from "../../api/user";
import { railsToIana } from '../../utils/timezones';
import { useNavigate } from "react-router-dom";
import TradeProposalAccordion from "./TradeProposalAccordion";
import {
  RECENT_TRADE_EVENT_WINDOW_MS,
  TRADE_NOTICE_STORAGE_KEY,
  TRADE_STATS_WINDOWS,
  buildTradeNoticeFromEvent,
  buildTradeNoticeFromProposal,
  buildTradeRows,
  buildTradeStatsItems,
  computeTradeStats,
  filterClosedTradesForStats,
  getNormalizedStatus,
  partitionTradeProposalsByStatus,
  sortTradeProposals,
  toNonEmptyString,
} from "./tradeProposalPanelModel";

const SIDEBAR_ACCOUNT_FOOTER_SAFE_GAP = 16; //don't change, this is the correct value.

export default function TradeProposalsPanel({
  userData,
  onQuestionSubmit,
  setActivePanel,
}) {
  const userTimezone = railsToIana(userData?.user_account_info?.timezone);
  const navigate = useNavigate();
  const [tradeProposals, setTradeProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clearingScope, setClearingScope] = useState(null);
  const [trackingProposalId, setTrackingProposalId] = useState(null);
  const [highlightChatMessageId, setHighlightChatMessageId] = useState(null);
  const [statsWindow, setStatsWindow] = useState("7d");
  const [tradeNoticeQueue, setTradeNoticeQueue] = useState([]);
  const highlightTimeoutRef = useRef(null);
  const listRef = useRef(null);
  const seenTradeNoticeKeysRef = useRef(null);
  const proposalNoticeKeyByIdRef = useRef(new Map());
  const hydratedTradeNoticeStateRef = useRef(false);

  const readSeenTradeNoticeKeys = () => {
    if (seenTradeNoticeKeysRef.current) return seenTradeNoticeKeysRef.current;

    if (typeof window === "undefined") {
      seenTradeNoticeKeysRef.current = new Set();
      return seenTradeNoticeKeysRef.current;
    }

    try {
      const raw = window.sessionStorage.getItem(TRADE_NOTICE_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      seenTradeNoticeKeysRef.current = new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      seenTradeNoticeKeysRef.current = new Set();
    }

    return seenTradeNoticeKeysRef.current;
  };

  const persistSeenTradeNoticeKeys = () => {
    if (typeof window === "undefined") return;
    const seen = Array.from(readSeenTradeNoticeKeys()).slice(-100);
    window.sessionStorage.setItem(TRADE_NOTICE_STORAGE_KEY, JSON.stringify(seen));
  };

  const triggerTradeHighlight = (chatMessageId) => {
    if (chatMessageId == null) return;
    const nextId = String(chatMessageId);
    setHighlightChatMessageId(nextId);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightChatMessageId((current) =>
        current === nextId ? null : current,
      );
    }, 6000);
  };

  const enqueueTradeNotice = (notice) => {
    if (!notice?.key) return;

    const seen = readSeenTradeNoticeKeys();
    if (seen.has(notice.key)) return;

    seen.add(notice.key);
    persistSeenTradeNoticeKeys();

    if (notice.chatMessageId != null) {
      triggerTradeHighlight(notice.chatMessageId);
    }

    setTradeNoticeQueue((prev) => {
      if (prev.some((item) => item.key === notice.key)) {
        return prev;
      }
      return [...prev, notice];
    });
  };

  const syncTradeProposalNotices = (nextProposals) => {
    const previousNoticeKeys = proposalNoticeKeyByIdRef.current;
    const nextNoticeKeys = new Map();
    const candidateNotices = (Array.isArray(nextProposals) ? nextProposals : [])
      .map((proposal) => buildTradeNoticeFromProposal(proposal))
      .filter((notice) => notice && Number.isFinite(notice.eventAtMs))
      .filter((notice) => notice.eventAtMs >= (Date.now() - RECENT_TRADE_EVENT_WINDOW_MS))
      .sort((left, right) => right.eventAtMs - left.eventAtMs);

    candidateNotices.forEach((notice) => {
      nextNoticeKeys.set(String(notice.proposalId), notice.key);
    });

    const isInitialHydration = !hydratedTradeNoticeStateRef.current;
    const noticesToQueue = candidateNotices.filter((notice) => {
      const previousKey = previousNoticeKeys.get(String(notice.proposalId));
      return previousKey !== notice.key;
    });

    proposalNoticeKeyByIdRef.current = nextNoticeKeys;
    hydratedTradeNoticeStateRef.current = true;

    if (isInitialHydration) {
      if (noticesToQueue[0]) enqueueTradeNotice(noticesToQueue[0]);
      return;
    }

    noticesToQueue.forEach(enqueueTradeNotice);
  };

  const fetchTrades = async () => {
    if (!userData?.user_id) return;
    setLoading(true);
    const res = await getUserTradeProposals();
    if (res && !res.error) {
      const nextTrades = Array.isArray(res) ? res : [];
      setTradeProposals(nextTrades);
      syncTradeProposalNotices(nextTrades);
    } else {
      setTradeProposals([]);
      syncTradeProposalNotices([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!userData?.user_id) return;

    fetchTrades();
  }, [userData?.user_id]);

  useEffect(() => {
    const handler = () => fetchTrades();
    window.addEventListener("trade-proposals:updated", handler);
    return () => window.removeEventListener("trade-proposals:updated", handler);
  }, [userData?.user_id]);

  const handleClearTrades = async (scope) => {
    if (!userData?.user_id || clearingScope) return;

    const isPastScope = scope === "past";
    const tradeCount = Array.isArray(tradeProposals)
      ? tradeProposals.filter((proposal) => {
          const isClosed = getNormalizedStatus(proposal) === "closed";
          return isPastScope ? isClosed : !isClosed;
        }).length
      : 0;
    if (tradeCount <= 0) return;

    const tradeLabel = tradeCount === 1 ? "trade" : "trades";
    const confirmationMessage = isPastScope
      ? `Clear ${tradeCount} past ${tradeLabel} from this panel?`
      : `Clear ${tradeCount} current ${tradeLabel} from this panel?`;
    if (typeof window !== "undefined" && !window.confirm(confirmationMessage)) {
      return;
    }

    setClearingScope(scope);
    try {
      const result = await clearUserTradeProposals(scope);
      if (result?.error) {
        console.error("Failed to clear trade proposals:", result.error);
        return;
      }

      await fetchTrades();
    } finally {
      setClearingScope(null);
    }
  };

  const handleStopTrackingTrade = async (proposal) => {
    if (!proposal?.chatMessageId || trackingProposalId != null) return;

    const tradeLabel =
      toNonEmptyString(proposal.ticker) ||
      toNonEmptyString(proposal.name) ||
      "this trade";
    const confirmationMessage = `Stop tracking this ${tradeLabel} trade?`;
    if (typeof window !== "undefined" && !window.confirm(confirmationMessage)) {
      return;
    }

    setTrackingProposalId(proposal.id);
    try {
      const result = await updateTradeProposalTracking(proposal.chatMessageId, false);
      if (result?.error) {
        console.error("Failed to stop tracking trade proposal:", result.error);
        return;
      }

      await fetchTrades();
    } finally {
      setTrackingProposalId(null);
    }
  };

  useEffect(() => {
    const handler = (event) =>
      triggerTradeHighlight(event?.detail?.chatMessageId);
    window.addEventListener("trade-proposals:highlight", handler);

    if (typeof window !== "undefined") {
      const pending = window.__warpTradeProposalHighlight;
      if (pending?.chatMessageId != null) {
        triggerTradeHighlight(pending.chatMessageId);
        window.__warpTradeProposalHighlight = null;
      }
    }

    return () => {
      window.removeEventListener("trade-proposals:highlight", handler);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const notice = buildTradeNoticeFromEvent(event?.detail);
      if (notice) {
        enqueueTradeNotice(notice);
      }
    };

    window.addEventListener("trade-proposals:event", handler);
    return () => window.removeEventListener("trade-proposals:event", handler);
  }, []);

  useEffect(() => {
    if (!highlightChatMessageId || !listRef.current) return;
    const target = listRef.current.querySelector(
      `[data-chat-message-id="${highlightChatMessageId}"]`,
    );
    if (target?.scrollIntoView) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightChatMessageId, tradeProposals]);

  const sortedTradeProposals = useMemo(
    () => sortTradeProposals(tradeProposals),
    [tradeProposals],
  );

  const {
    pendingTradeProposals,
    activeTradeProposals,
    completedTradeProposals,
    openTradeProposals,
  } = useMemo(
    () => partitionTradeProposalsByStatus(sortedTradeProposals),
    [sortedTradeProposals],
  );

  const tradeRows = useMemo(
    () => buildTradeRows({
      pendingTradeProposals,
      activeTradeProposals,
      completedTradeProposals,
    }),
    [pendingTradeProposals, activeTradeProposals, completedTradeProposals],
  );

  const closedTradesForStats = useMemo(
    () => filterClosedTradesForStats(completedTradeProposals, statsWindow),
    [completedTradeProposals, statsWindow],
  );

  const tradeStats = useMemo(
    () => computeTradeStats(closedTradesForStats),
    [closedTradesForStats],
  );

  const statsItems = useMemo(
    () => buildTradeStatsItems(tradeStats),
    [tradeStats],
  );

  const tradePanelColors = isDarkMode
    ? {
        bg: "#131521",
        border: "rgba(255,255,255,0.08)",
        text: "#F6F6F6",
        subtext: "rgba(255,255,255,0.7)",
        changeProfit: "rgba(21, 128, 61, 0.84)",
        changeLoss: "rgba(185, 28, 28, 0.82)",
        divider: "rgba(255,255,255,0.08)",
        itemHover: "rgba(255,255,255,0.04)",
        highlightBg: "rgba(99,102,241,0.12)",
        highlightBorder: "rgba(129,140,248,0.7)",
        highlightShadow: "0 0 0 1px rgba(129,140,248,0.55)",
        pendingBg: "rgba(245,158,11,0.14)",
        pendingBorder: "rgba(245,158,11,0.5)",
        pendingText: "#FBBF24",
        completedWinBg: "rgba(34,197,94,0.14)",
        completedWinBorder: "rgba(34,197,94,0.55)",
        completedLossBg: "rgba(239,68,68,0.14)",
        completedLossBorder: "rgba(239,68,68,0.55)",
      }
      : {
        bg: "#F6F6F6",
        border: "#e5e7eb",
        text: "#1D1D1F",
        subtext: "rgba(0,0,0,0.6)",
        changeProfit: "#15803D",
        changeLoss: "#B91C1C",
        divider: "rgba(0,0,0,0.08)",
        itemHover: "rgba(0,0,0,0.04)",
        highlightBg: "rgba(59,130,246,0.08)",
        highlightBorder: "rgba(59,130,246,0.6)",
        highlightShadow: "0 0 0 1px rgba(59,130,246,0.45)",
        pendingBg: "rgba(245,158,11,0.12)",
        pendingBorder: "rgba(245,158,11,0.55)",
        pendingText: "#B45309",
        completedWinBg: "rgba(34,197,94,0.12)",
        completedWinBorder: "rgba(34,197,94,0.55)",
        completedLossBg: "rgba(239,68,68,0.12)",
        completedLossBorder: "rgba(239,68,68,0.55)",
      };

  return (
    <Box
      sx={{
        height: `calc(100% - ${SIDEBAR_ACCOUNT_FOOTER_SAFE_GAP}px - env(safe-area-inset-bottom, 0px))`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        px: 2,
        pt: 1,
        pb: 2,
      }}
    >
      <Box
        sx={{
          p: 1,
          borderRadius: 2,
          border: `1px solid ${tradePanelColors.border}`,
          backgroundColor: tradePanelColors.bg,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                color: tradePanelColors.text,
                fontWeight: 700,
                fontSize: "0.98rem",
              }}
            >
              Trade Actions
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate("/account")}
              sx={{
                textTransform: "none",
                whiteSpace: "normal",
                textAlign: "center",
                lineHeight: 1.15,
                flexShrink: 0,
                minWidth: 0,
                maxWidth: "100%",
                px: 1.2,
                py: 0.55,
                borderColor: tradePanelColors.divider,
                color: tradePanelColors.subtext,
                "&:hover": {
                  borderColor: tradePanelColors.highlightBorder,
                  backgroundColor: tradePanelColors.highlightBg,
                },
              }}
            >
              Notification settings
            </Button>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: tradePanelColors.subtext,
              lineHeight: 1.45,
              display: "block",
              mt: 0.5,
            }}
          >
            In-app trade notices stay on. Email and push can be managed in Account.
          </Typography>
        </Box>

        <Divider sx={{ mb: 1, borderColor: tradePanelColors.divider }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              pr: 0.5,
            }}
            ref={listRef}
          >
            {loading && (
              <Typography variant="caption" sx={{ color: tradePanelColors.subtext }}>
                Loading trade proposals…
              </Typography>
            )}

            {!loading && (!tradeProposals || tradeProposals.length === 0) && (
              <Typography variant="caption" sx={{ color: tradePanelColors.subtext }}>
                No trade proposals yet.
              </Typography>
            )}

            {!loading && sortedTradeProposals.length > 0 && tradeRows.map((row) => {
              if (row.kind === "section") {
                return (
                  <Box
                    key={row.key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                      mb: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: tradePanelColors.subtext,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        fontSize: "0.62rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.title}
                    </Typography>
                    <Divider sx={{ flex: 1, borderColor: tradePanelColors.divider }} />
                  </Box>
                );
              }

              return (
                <TradeProposalAccordion
                  key={row.key}
                  proposal={row.proposal}
                  highlightChatMessageId={highlightChatMessageId}
                  tradePanelColors={tradePanelColors}
                  userTimezone={userTimezone}
                  onQuestionSubmit={onQuestionSubmit}
                  setActivePanel={setActivePanel}
                  trackingProposalId={trackingProposalId}
                  onStopTrackingTrade={handleStopTrackingTrade}
                />
              );
            })}
          </Box>

          <Box
            sx={{
              mt: 1,
              pt: 1,
              px: 0.25,
              borderTop: `1px solid ${tradePanelColors.divider}`,
            }}
          >
            <Box
              sx={{
                borderRadius: 1.5,
                border: `1px solid ${tradePanelColors.divider}`,
                backgroundColor: tradePanelColors.bg,
                px: 1,
                py: 0.9,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 0.9,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: tradePanelColors.subtext,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontSize: "0.62rem",
                    fontWeight: 600,
                  }}
                >
                  Trade Statistics
                </Typography>
                <FormControl size="small" sx={{ minWidth: 128 }}>
                  <Select
                    value={statsWindow}
                    onChange={(event) => setStatsWindow(event.target.value)}
                    sx={{
                      height: 30,
                      fontSize: "0.75rem",
                      color: tradePanelColors.text,
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                      "& .MuiSvgIcon-root": {
                        color: tradePanelColors.subtext,
                      },
                      "& fieldset": {
                        borderColor: tradePanelColors.divider,
                      },
                      "&:hover fieldset": {
                        borderColor: tradePanelColors.highlightBorder,
                      },
                    }}
                  >
                    {TRADE_STATS_WINDOWS.map((windowOption) => (
                      <MenuItem key={windowOption.value} value={windowOption.value}>
                        {windowOption.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 0.7,
                }}
              >
                {statsItems.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      borderRadius: 1,
                      border: `1px solid ${tradePanelColors.divider}`,
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                      px: 0.75,
                      py: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: tradePanelColors.subtext,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        fontSize: "0.54rem",
                        display: "block",
                        lineHeight: 1.2,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: tradePanelColors.text,
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        display: "block",
                        mt: 0.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 0.75, mt: 0.8 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    clearingScope !== null || completedTradeProposals.length === 0
                  }
                  onClick={() => handleClearTrades("past")}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    fontSize: "0.7rem",
                    minHeight: 28,
                    borderColor: tradePanelColors.divider,
                    color: tradePanelColors.subtext,
                    "&:hover": {
                      borderColor: tradePanelColors.highlightBorder,
                      backgroundColor: tradePanelColors.highlightBg,
                    },
                  }}
                >
                  {clearingScope === "past" ? "Clearing..." : "Clear past trades"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    clearingScope !== null || openTradeProposals.length === 0
                  }
                  onClick={() => handleClearTrades("current")}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    fontSize: "0.7rem",
                    minHeight: 28,
                    borderColor: tradePanelColors.divider,
                    color: tradePanelColors.subtext,
                    "&:hover": {
                      borderColor: tradePanelColors.highlightBorder,
                      backgroundColor: tradePanelColors.highlightBg,
                    },
                  }}
                >
                  {clearingScope === "current" ? "Clearing..." : "Clear current trades"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        key={tradeNoticeQueue[0]?.key || "trade-notice"}
        open={tradeNoticeQueue.length > 0}
        autoHideDuration={6500}
        onClose={() => {
          setTradeNoticeQueue((prev) => prev.slice(1));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => {
            setTradeNoticeQueue((prev) => prev.slice(1));
          }}
          severity={tradeNoticeQueue[0]?.severity || "info"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          <strong>{tradeNoticeQueue[0]?.title || "Trade update"}</strong>
          <Box component="span" sx={{ display: "block", mt: 0.2 }}>
            {tradeNoticeQueue[0]?.message || "A tracked trade changed status."}
          </Box>
        </Alert>
      </Snackbar>
    </Box>
  );
}
